import React, { useState, useCallback } from 'react';
import { Row, Col, FormGroup, Input, InputGroup, InputGroupAddon, Button } from 'reactstrap';
import { getProperByMailEndpoint } from '../../../services/publicService';
import useAdminEntity from '../../../hooks/useAdminQuotationEntity';
import { AsesorInfo, AsesorInfoSectionProps, AsesorSearchProps } from '../interfaces/asesor.interface';
import { ProperResponse } from '../interfaces/api-responses.interface';

// ============ SHARED COMPONENTS ============
interface FieldProps {
  label: string;
  type?: string;
  placeholder: string;
  value: string | number;
  onChange: (value: string) => void;
  readOnly?: boolean;
  colSize?: string;
  symbolData?: string;
}

const Field: React.FC<FieldProps> = ({ 
  label, 
  type = 'text', 
  placeholder, 
  value, 
  onChange, 
  readOnly = false,
  colSize = '4',
  symbolData
}) => {
  const displayValue = (() => {
    if (type === 'number' && (value === 0 || value === '0')) {
      return '';
    }
    return value;
  })();
  
  // Determinar si el input tiene contenido (excluir ceros para números)
  const hasContent = (() => {
    if (type === 'number') {
      return value !== '' && value !== 0 && value !== '0' && value !== null && value !== undefined;
    }
    return value !== '' && value !== null && value !== undefined;
  })();
  
  // Estilos dinámicos para el input
  const inputStyles = {
    ...(readOnly ? { backgroundColor: '#f8f9fa' } : {}),
    ...(hasContent ? { color: '#000000', fontWeight: '500' } : {})
  };

  return (
    <Col md={colSize} className="mb-3">
      <FormGroup>
        <label className="form-control-label">{label}</label>
        <Input
          type={type as any}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={inputStyles}
        />
      </FormGroup>
    </Col>
  );
};

interface SectionProps {
  title: string;
  borderColor: string;
  children: React.ReactNode;
  backgroundColor?: string;
}

const Section: React.FC<SectionProps> = ({ 
  title, 
  borderColor, 
  children, 
  backgroundColor = '#f8f9fa' 
}) => (
  <div style={{
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px',
    backgroundColor
  }}>
    <h5 style={{
      marginBottom: '20px',
      color: '#495057',
      borderBottom: `2px solid ${borderColor}`,
      paddingBottom: '8px'
    }}>
      {title}
    </h5>
    {children}
  </div>
);

// ============ SEARCH COMPONENT ============
const AsesorSearch: React.FC<AsesorSearchProps> = ({ onAsesorFound, onError, token }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);

  const getProperData = useCallback(() => {
    if (!searchEmail.trim() || !shouldSearch) {
      return null;
    }
    return getProperByMailEndpoint(searchEmail, token);
  }, [searchEmail, token, shouldSearch]);

  const setProperInfo = useCallback(
    (properData: ProperResponse) => {      
      const asesorInfo: AsesorInfo = {
        proper_id: properData.proper_id || properData.id || `temp_asesor_${Date.now()}`,
        nombre: properData.name?.trim() || '',
        email: properData.email
      };
      
      onAsesorFound(asesorInfo);
      setShouldSearch(false);
      setSearchEmail('');
    },
    [onAsesorFound]
  );

  const handleSearchError = useCallback(
    (errorMessage: string) => {
      if (shouldSearch) {
        onError('Asesor no encontrado o error en la búsqueda');
      }
      setShouldSearch(false);
    },
    [onError, shouldSearch]
  );

  const [, , isSearching] = useAdminEntity<ProperResponse>(
    getProperData,
    handleSearchError,
    setProperInfo
  );

  const handleSearch = useCallback(() => {
    if (!searchEmail.trim()) {
      onError('Por favor ingrese un email para buscar el asesor');
      return;
    }
    setShouldSearch(true);
  }, [searchEmail, onError]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchEmail(value);
    if (!value.trim() && shouldSearch) {
      setShouldSearch(false);
    }
  }, [shouldSearch]);

  // Determinar si el input de búsqueda tiene contenido
  const hasSearchContent = searchEmail.trim() !== '';

  return (
    <Row className="mb-4">
      <Col md="8">
        <FormGroup>
          <label className="form-control-label">Buscar asesor por email</label>
          <InputGroup>
            <Input
              type="email"
              placeholder="asesor@empresa.com"
              value={searchEmail}
              onChange={handleEmailChange}
              onKeyPress={handleKeyPress}
              disabled={isSearching}
              style={{
                ...(hasSearchContent ? { color: '#000000', fontWeight: '500' } : {})
              }}
            />
            <InputGroupAddon addonType="append">
              <Button 
                color="info" 
                onClick={handleSearch}
                disabled={isSearching || !searchEmail.trim()}
              >
                {isSearching ? 'Buscando...' : 'Buscar'}
              </Button>
            </InputGroupAddon>
          </InputGroup>
          {isSearching && shouldSearch && (
            <small className="text-muted mt-1">
              Buscando asesor por email: {searchEmail}...
            </small>
          )}
        </FormGroup>
      </Col>
    </Row>
  );
};

// ============ MAIN COMPONENT ============
const AsesorInformationQuotationComponent: React.FC<AsesorInfoSectionProps> = ({ 
  asesorInfo, 
  onAsesorChange,
  onAsesorFound,
  onError,
  token
}) => (
  <Section title="Información del Asesor" borderColor="#17a2b8" backgroundColor="#fff">
    <AsesorSearch
      onAsesorFound={onAsesorFound}
      onError={onError}
      token={token}
    />
    
    <Row>
      <Field
        label="Nombre asesor"
        placeholder="Nombre del asesor"
        value={asesorInfo.nombre}
        onChange={(v) => onAsesorChange('nombre', v)}
        colSize="6"
      />
      <Field
        label="Email asesor"
        type="email"
        placeholder="Email del asesor"
        value={asesorInfo.email}
        onChange={(v) => onAsesorChange('email', v)}
        colSize="6"
      />
    </Row>
  </Section>
);

export default AsesorInformationQuotationComponent;